'use client';

import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import logo from '@/assets/icons/logo.svg';
import craftedByLogo from '@/assets/icons/quadroom-logo.svg';
import { EmailIcon } from '@/assets/react-icons/EmailIcon';
import { PhoneIcon } from '@/assets/react-icons/PhoneIcon';
import { PinIcon } from '@/assets/react-icons/PinIcon';
import { Button } from '@/components/Button';
import { CMSLink } from '@/components/CMSLink';
import { Container } from '@/components/Container';
import { Copy } from '@/components/Copy';
import { Modal } from '@/components/Modal';
import { Responsive } from '@/components/Responsive';
import { Text } from '@/components/Text';
import { useUser } from '@/contexts/AuthProvider';
import { useGlobals } from '@/contexts/GlobalsContext';
import { Calculator } from '../Header/Calculator';
import styles from './Footer.module.scss';
export const getYear = () => {
  const d = new Date();
  return d.getFullYear();
};
export const Footer = () => {
  const {
    footer,
    setShowConsultationModal
  } = useGlobals();
  const {
    addresses,
    buttonLabel,
    description,
    email,
    menu,
    phones
  } = footer;
  const user = useUser();
  const isAuth = !!user;
  const [isOpenModal, setOpenModal] = useState(false);
  const scrollToTop = () => {
    document.body.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <footer className={styles.wrapper} data-sentry-component="Footer" data-sentry-source-file="Footer.tsx">
      <Container data-sentry-element="Container" data-sentry-source-file="Footer.tsx">
        <div className={styles['content-wrapper']}>
          <div className={styles['left']}>
            <div className={styles['logo-wrapper']}>
              <Link className={styles['logo']} href={'/'} data-sentry-element="Link" data-sentry-source-file="Footer.tsx">
                <Image alt='logo' src={logo} data-sentry-element="Image" data-sentry-source-file="Footer.tsx" />
              </Link>
              <Text color='text-gray' style={{
              whiteSpace: 'pre-wrap'
            }} type='p4' data-sentry-element="Text" data-sentry-source-file="Footer.tsx">
                {description}
              </Text>
              <Button bordered className={styles['btn-consult']} onClick={() => setShowConsultationModal(true)} small data-sentry-element="Button" data-sentry-source-file="Footer.tsx">
                {buttonLabel}
              </Button>
            </div>
            <nav className={styles['menu-wrapper']}>
              {menu?.map(({
              id,
              link
            }) => {
              if (!isAuth && link.url === '/offer/create') {
                return <Link className={styles['menu-item']} href={'/auth/sign-in'} key={id}>
                      <Text type={'button_large'}>{link.label}</Text>
                    </Link>;
              }
              if (link.isCalculator) {
                return <Modal className={styles['modal-content-calculator']} key={id} onOpenChange={setOpenModal} open={isOpenModal} trigger={<button className={styles['menu-item']}>
                          <Text type={'button_large'}>{link.label}</Text>
                        </button>}>
                      <Calculator setOpenCalculator={setOpenModal} />
                    </Modal>;
              }
              return <CMSLink className={clsx(styles['menu-item'])} key={id} link={link}>
                    <Text color={'black'} type={'button_large'}>
                      {link.label}
                    </Text>
                  </CMSLink>;
            })}
            </nav>
          </div>
          <div className={styles['right']}>
            <div className={styles['phones-wrapper']}>
              {phones?.map(({
              id,
              label,
              phone
            }) => <Link className={styles['phone-item']} href={`tel:${phone}`} key={id}>
                  <div className={styles['icon-wrapper']}>
                    <PhoneIcon />
                  </div>
                  <Text type='p4'>
                    {label}
                    {phone}
                  </Text>
                </Link>)}
            </div>
            <div className={styles['address-wrapper']}>
              <Responsive hideFrom='laptop' data-sentry-element="Responsive" data-sentry-source-file="Footer.tsx">
                <Link className={styles['email-item']} href={`mailto:${email}`} data-sentry-element="Link" data-sentry-source-file="Footer.tsx">
                  <EmailIcon data-sentry-element="EmailIcon" data-sentry-source-file="Footer.tsx" />
                  <Text type='p4' data-sentry-element="Text" data-sentry-source-file="Footer.tsx">{email}</Text>
                </Link>
              </Responsive>
              <Responsive showFrom='laptop' data-sentry-element="Responsive" data-sentry-source-file="Footer.tsx">
                <Copy className={styles['email-item']} copiedMessage='Email скопійовано' textToCopy={email ?? ''} data-sentry-element="Copy" data-sentry-source-file="Footer.tsx">
                  <EmailIcon data-sentry-element="EmailIcon" data-sentry-source-file="Footer.tsx" />
                  <Text type='p4' data-sentry-element="Text" data-sentry-source-file="Footer.tsx">{email}</Text>
                </Copy>
              </Responsive>

              {addresses?.map(({
              id,
              label,
              link
            }) => <Link className={styles['address-item']} href={link ?? ''} key={id} target='_blank'>
                  <div className={styles['icon-wrapper']}>
                    <PinIcon />
                  </div>
                  <Text type='p4'>{label}</Text>
                </Link>)}
            </div>
          </div>
        </div>
        <div className={styles['footer-bottom']}>
          <div className={styles.footerBottomTop}>
            <Responsive className={styles.allRightText} hideFrom='tablet' data-sentry-element="Responsive" data-sentry-source-file="Footer.tsx">
              <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="Footer.tsx">
                © Доброзем, {getYear()}. All rights reserved
              </Text>
            </Responsive>
            <div className={styles['btn-up']} onClick={scrollToTop}>
              <svg fill='none' height='32' viewBox='0 0 32 32' width='32' xmlns='http://www.w3.org/2000/svg' data-sentry-element="svg" data-sentry-source-file="Footer.tsx">
                <path clipRule='evenodd' d='M5.57227 19.0943L8.05025 21.5723L15.5723 14.0503L23.0943 21.5723L25.5723 19.0943L15.5723 9.09434L5.57227 19.0943Z' fill='black' fillRule='evenodd' data-sentry-element="path" data-sentry-source-file="Footer.tsx" />
              </svg>
            </div>
          </div>
          <div className={styles['privacy-wrapper']}>
            <div className={styles.footerBottomLeft}>
              <Responsive showFrom='tablet' data-sentry-element="Responsive" data-sentry-source-file="Footer.tsx">
                <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="Footer.tsx">
                  © Доброзем, {getYear()}. All rights reserved
                </Text>
              </Responsive>
              <Link className={styles['privacy-link']} href={'/privacy-policy'} data-sentry-element="Link" data-sentry-source-file="Footer.tsx">
                <Text type='p4' data-sentry-element="Text" data-sentry-source-file="Footer.tsx">Публічна оферта</Text>
              </Link>
            </div>
            <a className={styles.handcraftedBy} href='https://quadroom.agency/' rel='noopener noreferrer' target='_blank'>
              <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="Footer.tsx">
                Handcrafted by
              </Text>
              <Image alt='quadroom agency' src={craftedByLogo} data-sentry-element="Image" data-sentry-source-file="Footer.tsx" />
            </a>
          </div>
        </div>
      </Container>
    </footer>;
};